import React, { useRef, useState } from 'react'
import { Dialog, DialogContent, DialogTrigger, DialogClose } from 'src/components/dialogue/radix-dialog';
import { uploadProductThumbnail } from '../../api/product.api';
import { toast } from 'react-toastify';
import { uploadToGallery } from '../../api/gallery.api';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  children: React.ReactNode;
}

const UploadImageModal = ({children}: Props) => {

  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [description, setDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const queryClient = useQueryClient();


  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    const previews = selectedFiles.map((file) => URL.createObjectURL(file));

    setFiles((prev) => [...prev, ...selectedFiles]);
    setPreviewImages((prev) => [...previews, ...prev]);
  };

  const resetState = () => {
    setFiles([]);
    setPreviewImages([]);
    setDescription("");
  };

  const onImageUpload = async () => {
    if (!files.length) return toast.error("Select images!");
    if (!description.trim()) return toast.error("Add image description!");

    try {
      setIsUploading(true);

      for (const file of files) {
        const key = await uploadProductThumbnail(file);

        if (!key) continue;

        await uploadToGallery({
          s3_key: key,
          description,
        });
      }

      toast.success("Images uploaded!");

      queryClient.invalidateQueries({
        queryKey: ["adm-gallery-images"],
      });

      resetState();
    } catch (error) {
      toast.error("Upload failed!");
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="max-h-[80vh] w-full bg-white rounded-3xl max-w-2xl p-8 space-y-6">
        <h3 className="text-xl font-black text-center text-slate-800">
          Upload Image to Gallery
        </h3>

        {/* Upload Area */}
        <div
          onClick={() => galleryInputRef.current?.click()}
          className="cursor-pointer group w-full aspect-video bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-emerald-500 transition"
        >
          <p className="text-sm font-medium">Click to upload images</p>
          <p className="text-xs">PNG, JPG, WEBP (max 5MB)</p>

          <input
            ref={galleryInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>

        {/* Preview */}
        {previewImages.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {previewImages.map((img, idx) => (
              <div
                key={idx}
                className="aspect-square rounded-xl overflow-hidden border"
              >
                <img
                  src={img}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        )}

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-700">
            Image Name / Description
          </label>

          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Example: Product Banner"
            className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <DialogClose asChild>
            <button
              onClick={resetState}
              className="flex-1 border border-red-200 text-red-400 py-3 rounded-xl font-bold hover:bg-red-50"
            >
              Cancel
            </button>
          </DialogClose>

          <button
            onClick={onImageUpload}
            disabled={isUploading}
            className="flex-1 bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 active:scale-95 transition"
          >
            {isUploading ? "Uploading..." : "Save Images"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UploadImageModal;